import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Service {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  status: string;
  visibility: string;
}

interface ServiceDefinition {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
}

interface ServicesTabProps {
  escortId: string;
}

// Définir les options pour les selects
const statusOptions = [
  { value: 'included', label: 'escort.services.status.included' },
  { value: 'extra', label: 'escort.services.status.extra' },
  { value: 'refused', label: 'escort.services.status.refused' },
];

// Réutiliser les options de visibilité de RatesTab
const visibilityOptions = [
  { value: 'public', label: 'common.visibility.public' },
  { value: 'teaser', label: 'common.visibility.teaser' },
  { value: 'regular', label: 'common.visibility.regular' },
  { value: 'vip', label: 'common.visibility.vip' },
];

const ServicesTab: React.FC<ServicesTabProps> = ({ escortId }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [services, setServices] = useState<Service[]>([]);
  const [serviceDefinitions, setServiceDefinitions] = useState<ServiceDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [serviceToEdit, setServiceToEdit] = useState<Service | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Utiliser useCallback
  const loadServices = useCallback(async () => {
    if (!escortId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('escort_services')
        .select('*')
        .eq('escort_id', escortId)
        .order('name');

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      toast({
        title: t('escort.profile.error_loading_services'),
        description: t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [escortId, supabase, t, toast]);

  // Utiliser useCallback
  const loadServiceDefinitions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('service_definitions')
        .select('*')
        .order('name');

      if (error) throw error;
      setServiceDefinitions(data || []);
    } catch (error) {
      console.error('Error loading service definitions:', error);
    }
  }, [supabase]);

  useEffect(() => {
    loadServices();
    loadServiceDefinitions();
  }, [escortId, loadServices, loadServiceDefinitions]);

  // Schéma Zod affiné
  const formSchema = z.object({
    // Garder la possibilité d'entrer un nom custom ou de choisir via select
    name: z.string().min(2, { message: t('escort.profile.service_name_required') }),
    serviceDefinitionId: z.string().optional(), // ID du service prédéfini choisi
    description: z.string().optional(),
    price: z.coerce.number().nonnegative().optional(), // Prix peut être 0 ou positif, mais optionnel initialement
    status: z.enum(['included', 'extra', 'refused']), // Utiliser enum pour valider les statuts
    visibility: z.string(), // Laisser en string pour l'instant, validé par les options
  }).refine(data => {
    // Si le statut est 'extra', le prix doit être défini et strictement positif
    if (data.status === 'extra') {
      return data.price !== undefined && data.price > 0;
    }
    return true;
  }, {
    // Message d'erreur si la validation refine échoue
    message: t('escort.profile.service_price_required_for_extra'),
    path: ["price"], // Associer l'erreur au champ prix
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      serviceDefinitionId: '',
      description: '',
      price: undefined,
      status: 'included',
      visibility: 'public',
    },
  });

  // Récupérer la valeur du statut pour conditionner l'affichage/validation du prix
  const currentStatus = form.watch('status');

  // Effet pour mettre à jour le nom/description quand une définition est choisie
  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name === 'serviceDefinitionId' && value.serviceDefinitionId) {
        const selectedDef = serviceDefinitions.find(def => def.id === value.serviceDefinitionId);
        if (selectedDef) {
          form.setValue('name', selectedDef.name);
          // Optionnel: remplir aussi la description?
          // form.setValue('description', selectedDef.description || '');
        }
      }
      // Si le statut n'est pas 'extra', vider le prix
      if (name === 'status' && value.status !== 'extra') {
          form.setValue('price', undefined);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, serviceDefinitions]);

  const resetForm = () => {
    form.reset({
      name: '',
      serviceDefinitionId: '',
      description: '',
      price: undefined,
      status: 'included',
      visibility: 'public',
    });
    setServiceToEdit(null);
  };

  const openDialog = (service?: Service) => {
    if (service) {
      setServiceToEdit(service);
      form.reset({
        name: service.name,
        serviceDefinitionId: '', // Pas de lien direct à la définition lors de l'édition pour l'instant
        description: service.description || '',
        price: service.price ?? undefined,
        status: service.status as 'included' | 'extra' | 'refused',
        visibility: service.visibility,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const onSubmit = async (data: FormValues) => {
    try {
       // Ajuster le prix à null si le statut n'est pas 'extra'
       const finalPrice = data.status === 'extra' ? data.price : null;

       const serviceData = {
           name: data.name,
           description: data.description,
           price: finalPrice,
           status: data.status,
           visibility: data.visibility,
           // category: ? D'où vient la catégorie? Peut-être de la définition?
       };

      if (serviceToEdit) {
        // Mettre à jour le service existant
        const { error } = await supabase
          .from('escort_services')
          .update(serviceData)
          .eq('id', serviceToEdit.id);

        if (error) throw error;

        toast({
          title: t('escort.profile.service_updated'),
          description: t('escort.profile.service_update_success'),
        });
      } else {
        // Créer un nouveau service
        const { error } = await supabase
          .from('escort_services')
          .insert({
            escort_id: escortId,
            ...serviceData
          });

        if (error) throw error;

        toast({
          title: t('escort.profile.service_added'),
          description: t('escort.profile.service_add_success'),
        });
      }

      // Rafraîchir la liste et fermer la boîte de dialogue
      loadServices();
      closeDialog();
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: t('escort.profile.error_saving_service'),
        description: (error instanceof Error && error.message) || t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    }
  };

  const deleteService = async (serviceId: string) => {
    // Ajouter une confirmation
    if (!window.confirm(t('common.confirm_delete_item'))) {
       return;
     }
    try {
      const { error } = await supabase
        .from('escort_services')
        .delete()
        .eq('id', serviceId);

      if (error) throw error;

      toast({
        title: t('escort.profile.service_deleted'),
        description: t('escort.profile.service_delete_success'),
      });

      // Rafraîchir la liste de services
      loadServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: t('escort.profile.delete_error'),
        description: t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'included':
        return <Badge className="bg-green-500">{t('escort.services.status.included')}</Badge>;
      case 'extra':
        return <Badge className="bg-blue-500">{t('escort.services.status.extra')}</Badge>;
      case 'refused': 
        return <Badge variant="destructive">{t('escort.services.status.refused')}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getVisibilityBadge = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return <Badge variant="outline" className="bg-slate-200 text-slate-800">{t('common.visibility.public')}</Badge>;
      case 'teaser':
        return <Badge variant="outline" className="bg-purple-100 text-purple-800">{t('common.visibility.teaser')}</Badge>;
      case 'regular':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">{t('common.visibility.regular')}</Badge>;
      case 'vip':
        return <Badge variant="outline" className="bg-amber-100 text-amber-800">{t('common.visibility.vip')}</Badge>;
      default:
        return <Badge variant="secondary">{visibility}</Badge>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{t('escort.profile.manage_services')}</h2>
          <p className="text-muted-foreground">{t('escort.profile.services_description')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()}>
              <Plus size={18} className="mr-2" />
              {t('escort.profile.add_service')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>
                {serviceToEdit ? t('escort.profile.edit_service') : t('escort.profile.add_service')}
              </DialogTitle>
              <DialogDescription>
                {t('escort.profile.service_dialog_description')}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
                {/* Service Definition Select */}
                {serviceDefinitions.length > 0 && (
                    <FormField
                      control={form.control}
                      name="serviceDefinitionId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('escort.profile.select_predefined_service')}</FormLabel>
                          <Select 
                            onValueChange={field.onChange} 
                            defaultValue={field.value} 
                            // Reset name if 'custom' is selected
                            // onValueChange={(value) => {
                            //   field.onChange(value === 'custom' ? '' : value);
                            //   if (value === 'custom') form.setValue('name', '');
                            // }}
                            >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t('escort.profile.select_or_custom')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="">{t('escort.profile.custom_service')}</SelectItem> 
                              {serviceDefinitions.map((def) => (
                                <SelectItem key={def.id} value={def.id}>
                                  {def.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                )}

                {/* Service Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('escort.profile.service_name')} *</FormLabel>
                      <FormControl>
                        {/* Disable name input if a definition is selected */}
                        <Input 
                          placeholder={t('escort.profile.service_name_placeholder')} 
                          {...field} 
                          disabled={!!form.watch('serviceDefinitionId')}
                          />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('escort.profile.service_description')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('escort.profile.service_description_placeholder')}
                          {...field}
                          value={field.value || ''} // Ensure value is controlled
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                 {/* Status Select */}
                 <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('escort.profile.service_status')} *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('escort.profile.select_status')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {statusOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {t(option.label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                
                {/* Conditional Price Input */}
                {currentStatus === 'extra' && (
                    <FormField
                      control={form.control}
                      name="price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('escort.profile.service_price')} (€) *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder={t('escort.profile.price_placeholder_positive')} 
                              step="0.01" // Allow decimals
                              {...field}
                              // Ensure value is handled correctly (number or empty string for input)
                              value={field.value ?? ''} 
                              onChange={e => field.onChange(e.target.value === '' ? undefined : parseFloat(e.target.value))}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                )}

                 {/* Visibility Select */}
                 <FormField
                    control={form.control}
                    name="visibility"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common.visibility.title')}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('common.select_visibility')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {visibilityOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {t(option.label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit">
                    {serviceToEdit ? t('common.update') : t('common.add')}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : services.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-6 flex flex-col items-center justify-center min-h-[200px]">
            <p className="text-center text-muted-foreground mb-4">
              {t('escort.profile.no_services')}
            </p>
            <Dialog>
              <DialogTrigger asChild>
                <Button onClick={() => openDialog()}>
                  <Plus size={18} className="mr-2" />
                  {t('escort.profile.add_first_service')}
                </Button>
              </DialogTrigger>
            </Dialog>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('escort.profile.service_name')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('escort.profile.service_description')}</TableHead>
                  <TableHead>{t('escort.profile.status')}</TableHead>
                  <TableHead>{t('escort.profile.visibility')}</TableHead>
                  <TableHead>{t('escort.profile.price')}</TableHead>
                  <TableHead className="text-right">{t('escort.profile.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell className="font-medium">{service.name}</TableCell>
                    <TableCell className="hidden md:table-cell max-w-xs truncate">
                      {service.description || '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(service.status)}</TableCell>
                    <TableCell>{getVisibilityBadge(service.visibility)}</TableCell>
                    <TableCell>
                      {service.price ? `${service.price} €` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDialog(service)}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteService(service.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ServicesTab;
